import { useMutation } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { resultsApi } from '@/api/endpoints/results';
import { schoolsApi } from '@/api/endpoints/schools';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { downloadBlob } from '@/utils/downloadBlob';

type ReportKey =
  // Termin 1
  | 'stage1-t1-results'
  | 'stage1-t1-participants'
  | 'stage1-t1-all-answers'
  | 'stage1-t1-wrong-answers'
  // Termin 2
  | 'stage1-t2-results'
  | 'stage1-t2-participants'
  | 'stage1-t2-all-answers'
  | 'stage1-t2-wrong-answers'
  // Etap 1 razem
  | 'stage1-participants'
  | 'stage1-combined-results'
  // Etap 2
  | 'stage2-results'
  | 'stage2-full-results'
  | 'stage2-participants'
  | 'stage2-all-answers'
  | 'stage2-wrong-answers'
  | 'stage2-practical-scores';

interface ReportMeta {
  key: ReportKey;
  label: string;
  description: string;
  filename: string;
  /** Plik XLSX z folderu public — zamiast generowania CSV z API. */
  staticPublicFile?: string;
}

const STATIC_T1_RESULTS_CSV = 'wyniki-etap-1-termin-1 (1).csv';
const STATIC_T1_ANSWERS_XLSX =
  'Udzielone Odpowiedzi_Turniej_Etap_1_Termin-1 do dokumentacji - poprawione (2).xlsx';

async function fetchStaticPublicFile(
  publicFilename: string,
  downloadFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  const base = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');
  const url = `${base}/${encodeURIComponent(publicFilename)}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Nie znaleziono pliku: ${publicFilename}`);
  }
  const blob = await response.blob();
  return { blob, filename: downloadFilename };
}

const REPORTS_TERMIN_1: ReportMeta[] = [
  {
    key: 'stage1-t1-results',
    label: 'Wyniki',
    description: 'Oficjalny ranking Etapu 1, Termin 1 (plik dokumentacyjny)',
    filename: 'wyniki-etap-1-termin-1.csv',
    staticPublicFile: STATIC_T1_RESULTS_CSV,
  },
  {
    key: 'stage1-t1-participants',
    label: 'Lista uczestników',
    description: 'Uczniowie, którzy ukończyli test Etapu 1, Termin 1 (z nazwą szkoły)',
    filename: 'uczestnicy_etap1_termin1.csv',
  },
  {
    key: 'stage1-t1-all-answers',
    label: 'Wszystkie odpowiedzi',
    description: 'Udzielone odpowiedzi z Etapu 1, Termin 1 (plik dokumentacyjny)',
    filename: 'udzielone-odpowiedzi-etap-1-termin-1.xlsx',
    staticPublicFile: STATIC_T1_ANSWERS_XLSX,
  },
  {
    key: 'stage1-t1-wrong-answers',
    label: 'Błędne odpowiedzi',
    description: 'Błędne odpowiedzi z Etapu 1, Termin 1',
    filename: 'zled_odp_etap1_termin1.csv',
  },
];

const REPORTS_TERMIN_2: ReportMeta[] = [
  {
    key: 'stage1-t2-results',
    label: 'Wyniki',
    description: 'Ranking ukończonych testów (punkty ↓, czas ↑)',
    filename: 'wyniki_etap1_termin2.csv',
  },
  {
    key: 'stage1-t2-participants',
    label: 'Lista uczestników',
    description: 'Uczniowie, którzy ukończyli test Etapu 1, Termin 2 (z nazwą szkoły)',
    filename: 'uczestnicy_etap1_termin2.csv',
  },
  {
    key: 'stage1-t2-all-answers',
    label: 'Wszystkie odpowiedzi',
    description: 'Odpowiedzi wyłącznie z Etapu 1, Termin 2',
    filename: 'wszystkie_odp_etap1_termin2.csv',
  },
  {
    key: 'stage1-t2-wrong-answers',
    label: 'Błędne odpowiedzi',
    description: 'Błędne odpowiedzi z Etapu 1, Termin 2',
    filename: 'zled_odp_etap1_termin2.csv',
  },
];

const REPORTS_ETAP_1_RAZEM: ReportMeta[] = [
  {
    key: 'stage1-participants',
    label: 'Lista uczestników',
    description: 'Uczniowie, którzy ukończyli test Etapu 1 — termin 1 lub 2 (z nazwą szkoły)',
    filename: 'uczestnicy_etap1.csv',
  },
  {
    key: 'stage1-combined-results',
    label: 'Wyniki',
    description: 'Ranking łączny z obu terminów — od najlepszego wyniku do najgorszego',
    filename: 'wyniki_etap1_lacznie.csv',
  },
];

const REPORTS_ETAP_2: ReportMeta[] = [
  {
    key: 'stage2-full-results',
    label: 'Wyniki łączne (teoretyczny + praktyczny)',
    description: 'Ranking Etapu 2 — suma punktów testu teoretycznego i praktycznego z czasami',
    filename: 'wyniki_lacznie_etap2.csv',
  },
  {
    key: 'stage2-results',
    label: 'Wyniki testu teoretycznego',
    description: 'Ranking ukończonych testów teoretycznych Etapu 2',
    filename: 'wyniki_teor_etap2.csv',
  },
  {
    key: 'stage2-practical-scores',
    label: 'Wyniki testu praktycznego',
    description: 'Wyniki egzaminu praktycznego Etapu 2 (punkty, czas, godziny)',
    filename: 'wyniki_praktyczne_etap2.csv',
  },
  {
    key: 'stage2-participants',
    label: 'Lista uczestników',
    description: 'Uczniowie, którzy ukończyli test teoretyczny i mają wprowadzony wynik praktyczny (z nazwą szkoły)',
    filename: 'uczestnicy_etap2.csv',
  },
  {
    key: 'stage2-all-answers',
    label: 'Wszystkie odpowiedzi',
    description: 'Odpowiedzi z Etapu 2',
    filename: 'wszystkie_odp_etap2.csv',
  },
  {
    key: 'stage2-wrong-answers',
    label: 'Błędne odpowiedzi',
    description: 'Błędne odpowiedzi z Etapu 2',
    filename: 'zled_odp_etap2.csv',
  },
];


async function fetchReport(key: ReportKey): Promise<{ blob: Blob; filename: string }> {
  const allGroups = [...REPORTS_TERMIN_1, ...REPORTS_TERMIN_2, ...REPORTS_ETAP_1_RAZEM, ...REPORTS_ETAP_2];
  const meta = allGroups.find((r) => r.key === key)!;

  if (meta.staticPublicFile) {
    return fetchStaticPublicFile(meta.staticPublicFile, meta.filename);
  }

  switch (key) {
    // Stage 1 Term 1 (wyniki + odpowiedzi → statyczne XLSX z /public)
    case 'stage1-t1-participants': return { blob: await schoolsApi.exportStudentsCsv(1, 1), filename: meta.filename };
    case 'stage1-t1-wrong-answers': return { blob: await resultsApi.exportWrongAnswersCsv(1, 1), filename: meta.filename };

    // Stage 1 Term 2
    case 'stage1-t2-results': return { blob: await resultsApi.exportStageCsv(1, 2), filename: meta.filename };
    case 'stage1-t2-participants': return { blob: await schoolsApi.exportStudentsCsv(1, 2), filename: meta.filename };
    case 'stage1-t2-all-answers': return { blob: await resultsApi.exportAllAnswersCsv(1, 2), filename: meta.filename };
    case 'stage1-t2-wrong-answers': return { blob: await resultsApi.exportWrongAnswersCsv(1, 2), filename: meta.filename };

    // Stage 1 Together
    case 'stage1-participants': return { blob: await schoolsApi.exportStudentsCsv(1), filename: meta.filename };
    case 'stage1-combined-results': return { blob: await resultsApi.exportCombinedStage1Csv(), filename: meta.filename };

    // Stage 2
    case 'stage2-full-results': return { blob: await resultsApi.exportStage2FullCsv(), filename: meta.filename };
    case 'stage2-results': return { blob: await resultsApi.exportStageCsv(2), filename: meta.filename };
    case 'stage2-practical-scores': return { blob: await resultsApi.exportPracticalScoresCsv(), filename: meta.filename };
    case 'stage2-participants': return { blob: await schoolsApi.exportStudentsCsv(2), filename: meta.filename };
    case 'stage2-all-answers': return { blob: await resultsApi.exportAllAnswersCsv(2), filename: meta.filename };
    case 'stage2-wrong-answers': return { blob: await resultsApi.exportWrongAnswersCsv(2), filename: meta.filename };

    default:
      throw new Error(`Nieobsługiwany raport: ${key}`);
  }
}

export const ExportReportsPanel = ({ readOnly = false }: { readOnly?: boolean }) => {
  const exportMutation = useMutation({
    mutationFn: fetchReport,
    onSuccess: ({ blob, filename }) => {
      downloadBlob(blob, filename);
      toast.success('Raport pobrany');
    },
    onError: () => toast.error('Nie udało się wygenerować raportu'),
  });

  const renderGroup = (title: string, group: ReportMeta[]) => (
    <div className="mb-6 space-y-3">
      <h3 className="text-lg font-bold text-gray-800">{title}</h3>
      {group.map((report) => {
        const isPending = exportMutation.isPending && exportMutation.variables === report.key;
        return (
          <div
            key={report.key}
            className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p className="text-sm font-semibold text-gray-800">{report.label}</p>
              <p className="text-xs text-gray-500">{report.description}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0 gap-1.5"
              disabled={exportMutation.isPending}
              onClick={() => exportMutation.mutate(report.key)}
            >
              <Download className="h-4 w-4" />
              {isPending ? 'Pobieranie…' : report.staticPublicFile ? 'Pobierz XLSX' : 'Pobierz CSV'}
            </Button>
          </div>
        );
      })}
    </div>
  );

  return (
    <Card id="raporty">
      <CardHeader>
        <CardTitle>Eksport raportów (CSV)</CardTitle>
        <p className="mt-1 text-sm font-normal text-gray-500">
          Pobierz wyniki, w tym zablokowanych i aktywnych testów, posegregowane na etapy i terminy.
        </p>
      </CardHeader>
      <CardContent>
        {!readOnly && renderGroup('Etap 1 — Łącznie', REPORTS_ETAP_1_RAZEM)}
        {!readOnly && renderGroup('Etap 1 — Termin 1', REPORTS_TERMIN_1)}
        {!readOnly && renderGroup('Etap 1 — Termin 2', REPORTS_TERMIN_2)}
        {renderGroup('Etap 2', REPORTS_ETAP_2)}
      </CardContent>
    </Card>
  );
};
